/* The app-shell FatSecret sync (issue #88).
 *
 * Until this existed the on-demand sync endpoint had exactly one caller: the
 * Sync-now button on SETTINGS. Food logged in FatSecret did not reach LOG or
 * MACROS until the 21:00 sweep, and no screen said so — the finding in
 * `docs/PRODUCTION_READINESS.md` ("The web app never triggers a FatSecret
 * pull"). This hook is the on-load path #24 assumed and never shipped: fire the
 * EXISTING `postSyncNow()` once per app mount, watch `/admin/sync/status` until
 * the run clears, and then tell the screens to re-read.
 *
 * Three rules, all inherited rather than invented here:
 *
 * 1. **The backend owns the debounce.** `/admin/sync/now` answers
 *    `recently_synced` inside its 90s window without touching FatSecret, so a
 *    reload, a second tab or a StrictMode double-mount cost one cheap request
 *    each. The only throttle added here is not re-firing within one mount.
 * 2. **Failure degrades to cached data plus a staleness hint, never an error.**
 *    Every screen already renders whatever the read endpoints return; a sync
 *    that cannot run must not add an error state on top. What it may add is one
 *    honest label — `staleLabel` — whose age comes from the server's own
 *    `last_fatsecret_sync` clock, the one piece of staleness the API exposes.
 *    When even that is unknowable the label says nothing, because an age it
 *    cannot support is a guess.
 * 3. **Refresh must not touch what was typed.** The epoch is consumed by the
 *    screens' load effects only — form state never keys off it, so a bump
 *    re-reads the panels and leaves every input exactly where the user left it.
 *
 * The poll cadence, budget and "is this error current" rule are imported from
 * SETTINGS rather than restated, so the two callers of this endpoint cannot
 * drift apart. (App already imports from that module — see `resolveLinkOutcome`
 * in App.tsx — and `ticker.ts` imports from MACROS; screens are where this
 * repo's shared decisions live.)
 */
import { createContext, useContext, useEffect, useState } from 'react'

import {
  ApiError,
  getSyncStatus,
  postSyncNow,
  type IsoDateTime,
  type SyncStatus,
} from '../api'
import {
  SYNC_POLL_INTERVAL_MS,
  SYNC_POLL_MAX_ATTEMPTS,
  syncErrorToReport,
} from '../screens/Settings'

/* ── State ───────────────────────────────────────────────────────────────── */

/**
 * Where the shell's sync stands. `idle` covers signed-out AND the cases with
 * nothing to say (server unconfigured, auth failed — the screens' own reads
 * report those already). `stale` is the one phase a screen may surface, and it
 * carries the server's `last_fatsecret_sync` clock — null when there is no
 * such clock to quote: never synced, unlinked, or the status read itself failed.
 */
export type ShellSyncPhase =
  | { phase: 'idle' }
  | { phase: 'syncing' }
  | { phase: 'fresh' }
  | { phase: 'stale'; lastSyncAt: IsoDateTime | null }

export interface ShellSync {
  /** Bumped when a watched run finishes — the signal to re-read screen data.
   *  Never bumped for `recently_synced`: the data screens read at mount was
   *  already inside the debounce window, so nothing changed. */
  epoch: number
  sync: ShellSyncPhase
}

const IDLE: ShellSync = { epoch: 0, sync: { phase: 'idle' } }

/* ── The staleness label ─────────────────────────────────────────────────── */

const MONTHS = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
]

function twoDigit(n: number): string {
  return String(n).padStart(2, '0')
}

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/**
 * The one sentence LOG/MACROS may add when the shell sync left them on cached
 * data, or null for silence. Same time-vs-date rule as `fatsecretPill` in
 * SETTINGS: a bare HH:MM is only honest on the day it happened.
 *
 * `now` is this browser's clock, and that is allowed here for the same reason
 * it is in the pill — it formats a timestamp for display and never decides
 * which day any data belongs to.
 *
 * Null when there is no `lastSyncAt`: an unlinked or never-synced user has no
 * FatSecret snapshot whose age could be claimed, and a failed status read
 * leaves the age unknowable — in both cases a label would be a guess, and the
 * screens' own error surfaces already cover a server that is down.
 */
export function staleLabel(sync: ShellSyncPhase, now: Date): string | null {
  if (sync.phase !== 'stale' || sync.lastSyncAt === null) return null

  const at = new Date(sync.lastSyncAt)
  // An unparseable timestamp is a backend bug, not a reason to print
  // "Invalid Date" in a pill. Same guard as the SETTINGS pill.
  if (Number.isNaN(at.getTime())) return null

  const when = sameLocalDay(at, now)
    ? `${twoDigit(at.getHours())}:${twoDigit(at.getMinutes())}`
    : `${MONTHS[at.getMonth()]} ${twoDigit(at.getDate())}`
  return `FATSECRET STALE ✦ LAST SYNC ${when}`
}

/* ── The run ─────────────────────────────────────────────────────────────── */

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/** Resolves after `ms`, or immediately if `signal` aborts first — so a poll
 *  loop outliving the app mount does not leak a live timer. Same shape as the
 *  SETTINGS sleep, which is private to that module. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const id = window.setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(id)
        resolve()
      },
      { once: true },
    )
  })
}

/** `refreshed` means "the data on screen may have changed — re-read it". */
type Report = (sync: ShellSyncPhase, refreshed: boolean) => void

/**
 * Watch `/admin/sync/status` until `sync_in_progress` clears, then report.
 * Reached from `started` (202) and from catching `sync_in_progress` (409) —
 * whoever holds the claim is fetching the same days, so watching their run is
 * the honest move, exactly as SOURCES.CFG's poll does.
 *
 * A finished run bumps the epoch whether it succeeded or failed: a partial
 * failure may still have written rows, and re-reading is the only way the
 * screens show whatever did land. The failure itself becomes a `stale` phase
 * (via `syncErrorToReport`, so a superseded error does not count), never an
 * on-screen error.
 */
async function watchSync(signal: AbortSignal, report: Report): Promise<void> {
  let lastSeen: SyncStatus | null = null
  for (let attempt = 0; attempt < SYNC_POLL_MAX_ATTEMPTS; attempt++) {
    await sleep(SYNC_POLL_INTERVAL_MS, signal)
    if (signal.aborted) return
    let status: SyncStatus
    try {
      status = await getSyncStatus(signal)
    } catch (error) {
      if (isAbort(error)) return
      // A read hiccup mid-poll is not the sync's failure — keep watching.
      continue
    }
    if (signal.aborted) return
    lastSeen = status
    if (!status.sync_in_progress) {
      report(
        syncErrorToReport(status) !== null
          ? { phase: 'stale', lastSyncAt: status.last_fatsecret_sync }
          : { phase: 'fresh' },
        true,
      )
      return
    }
  }
  // Budget exhausted with the run still going. Nothing is known to have
  // changed, so no bump — the data on screen predates a sync that has not
  // finished, which is exactly what `stale` says.
  report({ phase: 'stale', lastSyncAt: lastSeen?.last_fatsecret_sync ?? null }, false)
}

/** The sync could not run at all — put an age on the cached data if the status
 *  endpoint can supply one, and stay silent if it cannot. */
async function reportStale(signal: AbortSignal, report: Report): Promise<void> {
  try {
    const status = await getSyncStatus(signal)
    if (signal.aborted) return
    report({ phase: 'stale', lastSyncAt: status.last_fatsecret_sync }, false)
  } catch (error) {
    if (isAbort(error)) return
    report({ phase: 'stale', lastSyncAt: null }, false)
  }
}

async function runShellSync(signal: AbortSignal, report: Report): Promise<void> {
  report({ phase: 'syncing' }, false)
  try {
    const result = await postSyncNow(signal)
    if (signal.aborted) return
    if (result.status === 'recently_synced') {
      report({ phase: 'fresh' }, false)
      return
    }
    // status === 'started': the fetch is running in the background now.
    await watchSync(signal, report)
  } catch (error) {
    if (isAbort(error)) return
    if (error instanceof ApiError) {
      if (error.status === 409 && error.detail === 'sync_in_progress') {
        await watchSync(signal, report)
        return
      }
      // 503: no consumer credentials — there is no FatSecret data to be stale.
      // 401/403: the bearer is bad, and every screen's own read is failing
      // loudly right now; a staleness pill on top of those alerts says nothing.
      if (error.status === 503 || error.status === 401 || error.status === 403) {
        report({ phase: 'idle' }, false)
        return
      }
    }
    // relink_required, a network failure, anything else: cached data + hint.
    await reportStale(signal, report)
  }
}

/* ── The hook and its context ────────────────────────────────────────────── */

/**
 * Fire the shell sync once per mount (per sign-in, strictly: `enabled` turning
 * true is what starts it). Returns the epoch/phase pair App hands to the
 * screens through `ShellSyncContext`.
 */
export function useSyncOnLoad(enabled: boolean): ShellSync {
  const [state, setState] = useState<ShellSync>(IDLE)

  useEffect(() => {
    if (!enabled) {
      setState(IDLE)
      return
    }
    const controller = new AbortController()
    const report: Report = (sync, refreshed) => {
      if (controller.signal.aborted) return
      setState((current) => ({
        epoch: refreshed ? current.epoch + 1 : current.epoch,
        sync,
      }))
    }
    void runShellSync(controller.signal, report)
    return () => controller.abort()
  }, [enabled])

  return state
}

/** Default is IDLE so a screen rendered without the provider (tests, mostly)
 *  behaves exactly as it did before the shell sync existed. */
export const ShellSyncContext = createContext<ShellSync>(IDLE)

/** What the screens read: `epoch` goes into the load effects' deps, `sync`
 *  through `staleLabel` into at most one pill. */
export function useShellSync(): ShellSync {
  return useContext(ShellSyncContext)
}
